import Button from '@ingka/button';
import '@ingka/button/dist/style.css';
import '@ingka/focus/dist/style.css';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import '@ingka/modal/dist/style.css';
import '@ingka/svg-icon/dist/style.css';
import { ComponentIds } from '@inter-ikea-core/core';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import { ProductType, StabilityOutlineSystem } from '@inter-ikea-storageone/range';
import StabilityComponent from '@inter-ikea-storageone/range/src/core-api/components/StabilityComponent';
import { useCore } from 'contexts/core';
import { useAppDispatch } from 'ducks/hooks';
import { hideDoors } from 'ducks/layout';
import { getMarketProducts } from 'ducks/products';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import Box from 'ui/Box';
import Body from 'ui/Typography/Body';
import Title, { HeadingType } from 'ui/Typography/Title';
import {
  hideStabilitySheet,
  isModalVisible,
  setStabilityDismissedAndContinue
} from './StabilityValidation';
import { addShelvesToUnstableSections } from './StabilityValidationUtils';
import StabilityImageIvar from './ivar_stability_img.png';
import StabilityImageBror from './bror_stability_img.png';
import { rangeIsIvar } from 'utils/queryUtils';

const ModalFooterStyles = styled.div`
  .modal-footer--dual-action {
    display: block;
  }
`;

const Image = styled.img`
  width: 100%;
`;

const StabilityValidationSheet = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const products = useSelector(getMarketProducts);
  const coreApi = useCore();
  const shelves = useMemo(() => {
    return products.filter(product =>
      [
        ProductType.IVAR_SHELF_PINE,
        ProductType.IVAR_CORNER_SHELF,
        ProductType.BROR_SHELF_METAL_BLACK,
        ProductType.BROR_FOUR_PACK_SHELF,
        ProductType.ELVARLI_SHELF_WHITE
      ].includes(product.type)
    );
  }, [products]);
  const modalVisible = useSelector(isModalVisible);
  const stabilityImage = rangeIsIvar() ? StabilityImageIvar : StabilityImageBror;

  useEffect(() => {
    if (modalVisible && shelves.length > 0) {
      coreApi.validations['SeeThroughFramesValidation']?.disable();
      addShelvesToUnstableSections(shelves, coreApi, false);
      dispatch(hideDoors());
    }
  }, [coreApi, dispatch, modalVisible, shelves]);

  const onUseRecommendation = () => {
    const stabilityShelves = coreApi.entities.getByComponent(StabilityComponent.id);
    stabilityShelves.forEach(shelf => {
      shelf.removeComponent(ComponentIds.OUTLINED_COMPONENT_ID);
      shelf.removeComponent(StabilityComponent.id);
    });
    dispatch(hideStabilitySheet());
    coreApi.validations['SeeThroughFramesValidation']?.enable();
    dispatch(setStabilityDismissedAndContinue(true));
  };

  const onDismiss = () => {
    dispatch(hideStabilitySheet());
    coreApi.getSystem<StabilityOutlineSystem>('StabilityOutlineSystem')?.enable();
    coreApi.validations['SeeThroughFramesValidation']?.enable();
  };

  return (
    <Modal
      data-testid="stability-sheet"
      visible={!!modalVisible && shelves.length > 0}
      escapable={true}
      handleCloseBtn={onDismiss}
    >
      <Sheets
        title={formatMessage({ id: 'storageone.stabilitySheetTitle' })}
        header={
          <ModalHeader
            data-testid="modal-header"
            ariaCloseTxt={formatMessage({ id: 'storageone.closeButton' })}
          />
        }
        footer={
          <ModalFooter>
            <ModalFooterStyles>
              <Button
                text={formatMessage({ id: 'storageone.stabilitySheetPrimaryButton' })}
                onClick={onUseRecommendation}
                data-testid="stability-sheet-finalize-btn"
                type="primary"
                fluid
              />
              <Button
                text={formatMessage({ id: 'storageone.stabilitySheetSecondaryButton' })}
                onClick={onDismiss}
                data-testid="stability-sheet-continue-planning-btn"
                type="secondary"
                fluid
              />
            </ModalFooterStyles>
          </ModalFooter>
        }
      >
        <ModalBody>
          <Title
            mb="l"
            type={ThemeFontStyleTypeEnum.headingMedium}
            heading={HeadingType.HeadingTwo}
          >
            {formatMessage({ id: 'storageone.stabilitySheetTitle' })}
          </Title>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.stabilitySheetDescription' })}
          </Body>
          <Box px={'xxxxxl'} py={'xl'} style={{ width: '100%' }}>
            <Image
              src={stabilityImage}
              alt={formatMessage({ id: 'storageone.stabilitySheetDescription' })}
            />
          </Box>
          <Body type={ThemeFontStyleTypeEnum.bodyMedium}>
            {formatMessage({ id: 'storageone.stabilitySheetAdditionalDescription' })}
          </Body>
        </ModalBody>
      </Sheets>
    </Modal>
  );
};

export default StabilityValidationSheet;
